import type {
  StrapiCustomerCreateData,
  StrapiCustomerRow,
  StrapiCustomerWriteData,
} from '../../api/strapiCrm';

export type CustomerFormDraft = {
  customer_number: string;
  title_prefix: string;
  name: string;
  customer_type: string;
  active_status: string;
  npwp: string;
  address: string;
  alamat_lhu: string;
  no_telp_finance: string;
  phone: string;
  email: string;
  mobile_wa: string;
  city: string;
  province: string;
  postal_code: string;
  office_phone: string;
  fax: string;
  contact_name: string;
  job_title: string;
  work_email: string;
  discount: string;
  notes: string;
};

export function emptyCustomerFormDraft(): CustomerFormDraft {
  return {
    customer_number: '',
    title_prefix: '',
    name: '',
    customer_type: '',
    active_status: '',
    npwp: '',
    address: '',
    alamat_lhu: '',
    no_telp_finance: '',
    phone: '',
    email: '',
    mobile_wa: '',
    city: '',
    province: '',
    postal_code: '',
    office_phone: '',
    fax: '',
    contact_name: '',
    job_title: '',
    work_email: '',
    discount: '',
    notes: '',
  };
}

export function customerFormDraftFromRow(row: StrapiCustomerRow): CustomerFormDraft {
  return {
    customer_number: row.customer_number ?? '',
    title_prefix: row.title_prefix ?? '',
    name: row.name ?? '',
    customer_type: row.customer_type ?? '',
    active_status: row.active_status ?? '',
    npwp: row.npwp ?? '',
    address: row.address ?? '',
    alamat_lhu: row.alamat_lhu ?? '',
    no_telp_finance: row.no_telp_finance ?? '',
    phone: row.phone ?? '',
    email: row.email ?? '',
    mobile_wa: row.mobile_wa ?? '',
    city: row.city ?? '',
    province: row.province ?? '',
    postal_code: row.postal_code ?? '',
    office_phone: row.office_phone ?? '',
    fax: row.fax ?? '',
    contact_name: row.contact_name ?? '',
    job_title: row.job_title ?? '',
    work_email: row.work_email ?? '',
    discount: row.discount != null && Number.isFinite(row.discount) ? String(row.discount) : '',
    notes: row.notes ?? '',
  };
}

/** Kosong atau hanya tanda hubung (`-`) belum dianggap nomor pelanggan. */
export function isPlaceholderCustomerNumber(value: unknown): boolean {
  const s = String(value ?? '').trim();
  return s === '' || /^[-–—]+$/.test(s);
}

function nz(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function parseDiscount(raw: string): number | null {
  const t = raw.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Payload baru: nomor pelanggan di-set server (`CUS-#####`). */
export function draftToNewCustomerPayload(draft: CustomerFormDraft): StrapiCustomerCreateData {
  const { customer_number: _omit, ...rest } = draftToCustomerPayload(draft);
  return rest;
}

export function draftToCustomerPayload(draft: CustomerFormDraft): StrapiCustomerWriteData {
  const customer_number = draft.customer_number.trim() || `DRAFT-${Date.now()}`;
  const title_prefix: StrapiCustomerRow['title_prefix'] =
    draft.title_prefix === ''
      ? null
      : (draft.title_prefix as NonNullable<Exclude<StrapiCustomerRow['title_prefix'], null>>);
  const customer_type: StrapiCustomerRow['customer_type'] =
    draft.customer_type === ''
      ? null
      : (draft.customer_type as NonNullable<Exclude<StrapiCustomerRow['customer_type'], null>>);
  const active_status: StrapiCustomerRow['active_status'] =
    draft.active_status === ''
      ? null
      : (draft.active_status as NonNullable<Exclude<StrapiCustomerRow['active_status'], null>>);

  return {
    customer_number,
    name: draft.name.trim(),
    address: nz(draft.address),
    alamat_lhu: nz(draft.alamat_lhu),
    no_telp_finance: nz(draft.no_telp_finance),
    phone: nz(draft.phone),
    email: nz(draft.email),
    mobile_wa: nz(draft.mobile_wa),
    city: nz(draft.city),
    province: nz(draft.province),
    postal_code: nz(draft.postal_code),
    office_phone: nz(draft.office_phone),
    contact_name: nz(draft.contact_name),
    fax: nz(draft.fax),
    job_title: nz(draft.job_title),
    work_email: nz(draft.work_email),
    notes: nz(draft.notes),
    npwp: nz(draft.npwp),
    discount: parseDiscount(draft.discount),
    title_prefix,
    customer_type,
    active_status,
  };
}
